import { useState } from 'react';
import type {
  MeetingTemplateKind,
  MeetingTemplateScope,
} from '@wundo/shared/meetingTemplate.ts';
import { Button } from '../button/button.tsx';
import { SettingsSection } from '../settings/settings-section.tsx';
import type { MeetingTemplateEditorLabels } from './meeting-template-editor-labels.ts';
import type {
  MeetingTemplateCohortOption,
  MeetingTemplateDraft,
  MeetingTemplateTrackOption,
} from './meeting-template-types.ts';
import { TemplateDetails } from './template-details.tsx';
import {
  isTemplateDraftComplete,
  offersAudience,
  toMeetingTemplateDraft,
  toTemplateDraftState,
  withAddedTopic,
  type TemplateDraftState,
} from './template-draft.ts';
import { TemplateFormActions } from './template-form-actions.tsx';
import { TemplateTopics } from './template-topics.tsx';

export interface TemplateFormProps {
  /** The template being edited, or null while writing a new one. */
  template: MeetingTemplateDraft | null;
  /** Both empty leaves the audience field off the form. */
  tracks: MeetingTemplateTrackOption[];
  cohorts: MeetingTemplateCohortOption[];
  /** Says why there is no audience to pick instead of offering the field. */
  wholeOrganisationOnly: boolean;
  labels: MeetingTemplateEditorLabels;
  kind?: MeetingTemplateKind;
  kindEditable: boolean;
  /** Set, who the template is for is offered, starting from this answer. */
  scope?: MeetingTemplateScope;
  canDelete: boolean;
  /** Set, the delete stays off and this line says why. */
  deleteRefusal?: string;
  saving: boolean;
  error?: string;
  onSave: (draft: MeetingTemplateDraft) => void;
  onDelete?: () => void | Promise<void>;
  onCancel?: () => void;
}

/**
 * The whole template is saved at once: the order of the topics is part of
 * what it says, so there is nothing to save halfway.
 */
export function TemplateForm({
  template,
  tracks,
  cohorts,
  wholeOrganisationOnly,
  labels,
  kind,
  kindEditable,
  scope,
  canDelete,
  deleteRefusal,
  saving,
  error,
  onSave,
  onDelete,
  onCancel,
}: TemplateFormProps) {
  const [draft, setDraft] = useState(() => toTemplateDraftState(template, kind, scope));

  function change(patch: Partial<TemplateDraftState>) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  function save() {
    const withAudience = offersAudience(draft, tracks, cohorts) && !wholeOrganisationOnly;
    onSave(toMeetingTemplateDraft(draft, { withAudience }));
  }

  return (
    <>
      {onCancel && (
        <div className='mb-4'>
          <Button size='sm' variant='ghost' onClick={onCancel}>
            {labels.back}
          </Button>
        </div>
      )}

      <TemplateDetails
        draft={draft}
        onChange={change}
        tracks={tracks}
        cohorts={cohorts}
        wholeOrganisationOnly={wholeOrganisationOnly}
        labels={labels}
        kindEditable={kindEditable}
      />

      <SettingsSection title={labels.topicsTitle} description={labels.topicsHint}>
        <TemplateTopics
          topics={draft.topics}
          labels={labels}
          onChange={(topics) => change({ topics })}
        />
        <div className='px-5.5 py-4'>
          <Button
            size='sm'
            variant='quiet'
            onClick={() =>
              setDraft((current) => ({
                ...current,
                topics: withAddedTopic(current.topics),
              }))
            }
          >
            {labels.addTopic}
          </Button>
        </div>
      </SettingsSection>

      <TemplateFormActions
        template={template}
        complete={isTemplateDraftComplete(draft)}
        canDelete={canDelete}
        deleteRefusal={deleteRefusal}
        saving={saving}
        error={error}
        labels={labels}
        onSave={save}
        onDelete={onDelete}
      />
    </>
  );
}
